import { memo, type FC } from "react";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import type { SxProps, Theme } from "@mui/material/styles";

export type GeoApproach = "regions" | "countries" | "states";

const ALL_OPTIONS: GeoApproach[] = ["regions", "countries", "states"];
const OPTION_LABELS: Record<GeoApproach, string> = {
  regions: "Regions",
  countries: "Countries",
  states: "States",
};

type GeoApproachToggleProps = {
  value: GeoApproach;
  onChange: (next: GeoApproach) => void;
  sx?: SxProps<Theme>;
  /** Restricts which buttons render — the share page's "no segment
   * geography" case offers Regions/Countries only, never States. */
  options?: GeoApproach[];
};

export const GeoApproachToggle: FC<GeoApproachToggleProps> = memo(
  ({ value, onChange, sx, options = ALL_OPTIONS }) => {
    return (
      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={(_e, next: GeoApproach | null) => {
          if (!next) return;
          onChange(next);
        }}
        size="small"
        fullWidth
        sx={[
          {
            "& .MuiToggleButton-root": {
              textTransform: "none",
              fontWeight: 600,
              py: 0.75,
            },
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {options.map((option) => (
          <ToggleButton
            key={option}
            value={option}
            data-testid={`geo-approach-${option}-btn`}
          >
            {OPTION_LABELS[option]}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    );
  }
);

GeoApproachToggle.displayName = "GeoApproachToggle";
